import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import TextField from '../../components/formfields/TextField';
import RichSelectField from '../../components/formfields/RichSelectField';
import { UserCircle, ChevronDown, ChevronUp } from 'lucide-react';

const TRAVEL_NETWORKS = [
  { value: 'None', label: 'None' },
  { value: 'American Express Travel', label: 'American Express Travel' },
  { value: 'Cruise Planners', label: 'Cruise Planners' },
  { value: 'Ensemble Travel Group', label: 'Ensemble Travel Group' },
  { value: 'Signature Travel', label: 'Signature Travel' },
  { value: 'Virtuoso', label: 'Virtuoso' },
  { value: 'Indagare', label: 'Indagare' },
  { value: 'Serandipians', label: 'Serandipians' },
  { value: 'Other', label: 'Other' }
];

const ReferralCard = ({ 
  appliedReferral,
  isExpanded = true,
  onChangeReferralData 
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expanded, setExpanded] = useState(isExpanded);
  
  // Form state for travel agency fields
  const [referralData, setReferralData] = useState({
    workingWithAgency: 'no',
    travelNetwork: '',
    travelAgency: '',
    agentName: '',
    iataNumber: ''
  });


  const containerClassName = clsx(
    'rounded-lg border transition-all duration-200',
    {
      'bg-slate-800 border-slate-700': isDark,
      'bg-gray-100 border-gray-300': !isDark
    }
  );

  const headerClassName = clsx(
    'flex items-center justify-between p-4 cursor-pointer',
    {
      'hover:bg-slate-700': isDark,
      'hover:bg-gray-200': !isDark
    }
  );

  const titleClassName = clsx(
    'text-lg font-medium flex items-center gap-2',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const iconClassName = clsx(
    'w-4 h-4',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  const contentClassName = clsx(
    'px-4 py-2 pb-4 space-y-4',
    {
      'border-t border-slate-600': isDark && expanded,
      'bg-white border-t rounded-br-lg rounded-bl-lg border-gray-200': !isDark && expanded
    }
  );

  const appliedClassName = clsx(
    'p-3 rounded-lg border',
    {
      'bg-green-50 border-green-200': !isDark,
      'bg-green-900/20 border-green-600': isDark
    }
  );

  const appliedTextClassName = clsx(
    'text-sm font-medium',
    {
      'text-green-600': !isDark,
      'text-green-400': isDark
    }
  );

  const radioClassName = clsx(
    'w-4 h-4 border-2 rounded-full transition-all duration-200',
    {
      'border-slate-400': isDark,
      'border-gray-400': !isDark
    }
  );

  const labelClassName = clsx(
    'text-sm',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const questionClassName = clsx(
    'text-sm font-medium',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const handleChangeReferralData = useCallback((field, value) => {
    setReferralData((prev) => ({
      ...prev,
      [field]: value
    }));
    onChangeReferralData?.(referralData);
  }, [setReferralData, referralData]);


  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className={containerClassName}>
      <div className={headerClassName} onClick={toggleExpanded}>
        <div className="flex items-center gap-1">
          <UserCircle className="h-4 w-4" />
          <h3 className={titleClassName}>
            Referral
          </h3>
        </div>
        {expanded ? (
          <ChevronUp className={iconClassName} />
        ) : (
          <ChevronDown className={iconClassName} />
        )}
      </div>

      {expanded && (
        <div className={contentClassName}>
          {appliedReferral ? (
            <div className={appliedClassName}>
              <div className={appliedTextClassName}>
                Travel advisor information submitted
              </div>
              {appliedReferral.agentName && (
                <div className={clsx(
                  'text-xs mt-1',
                  {
                    'text-green-700': !isDark,
                    'text-green-300': isDark
                  }
                )}>
                  Agent: {appliedReferral.agentName}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="workingWithAgency"
                  name="workingWithAgency"
                  checked={referralData.workingWithAgency === 'yes'}
                  onChange={(e) => handleChangeReferralData('workingWithAgency', e.target.checked ? 'yes' : 'no')}
                  onBlur={(e) => handleChangeReferralData('workingWithAgency', e.target.checked ? 'yes' : 'no')}
                  className="w-4 h-4"
                />
                <label htmlFor="workingWithAgency" className={`${questionClassName} cursor-pointer`}>
                  I am working with a travel advisor as a part of this cruise booking.
                </label>
              </div>

              {/* Travel agency fields - only show if "Yes" is selected */}
              {referralData.workingWithAgency === 'yes' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <RichSelectField
                      id="travel-network"
                      label="Travel network"
                      value={referralData.travelNetwork}
                      valueKey="value"
                      onChange={(e) => handleChangeReferralData('travelNetwork', e.target.value)}
                      onBlur={(e) => handleChangeReferralData('travelNetwork', e.target.value)}
                      options={TRAVEL_NETWORKS}
                      placeholder="Select travel network"
                      required
                    />
                    
                    <TextField
                      id="travel-company"
                      label="Travel company"
                      value={referralData.travelCompany}
                      onChange={(e) => handleChangeReferralData('travelCompany', e.target.value)}
                      onBlur={(e) => handleChangeReferralData('travelCompany', e.target.value)}
                      placeholder="Enter travel company name"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <TextField
                      id="agent-name"
                      label="Travel Advisor"
                      value={referralData.agentName}
                      onChange={(e) => handleChangeReferralData('agentName', e.target.value)}
                      onBlur={(e) => handleChangeReferralData('agentName', e.target.value)}
                      placeholder="Enter travel advisor"
                      required
                    />
                    
                    <TextField
                      id="iata-number"
                      label="IATA number"
                      value={referralData.iataNumber}
                      onChange={(e) => handleChangeReferralData('iataNumber', e.target.value)}
                      onBlur={(e) => handleChangeReferralData('iataNumber', e.target.value)}
                      placeholder="Enter IATA number"
                    />
                  </div>
                </div>
              )}

              {/* <button
                onClick={handleApplyReferral}
                disabled={!canApply || isApplying}
                className={buttonClassName}
              >
                {isApplying ? 'Submitting...' : 'Submit'}
              </button> */}
            </>
          )}
        </div>
      )}
    </div>
  );
};

ReferralCard.propTypes = {
  onApplyReferral: PropTypes.func,
  appliedReferral: PropTypes.shape({
    agentName: PropTypes.string,
    travelAgency: PropTypes.string,
    workingWithAgency: PropTypes.string
  }),
  isExpanded: PropTypes.bool,
  onChangeReferralData: PropTypes.func.isRequired
};

export default ReferralCard;